import { articleMetaBySlug } from "./articles/meta";

export type RolloutMode = "private" | "public";

export type RouteMeta = {
  title: string;
  description: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
};

export const SITE_NAME = "PX7 Training";
export const SITE_ORIGIN = "https://px7.training";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-cover.png`;

function normalizePath(pathname: string): string {
  if (!pathname) return "/";
  if (pathname === "/") return "/";
  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

function staticRouteMeta(rolloutMode: RolloutMode): Record<string, RouteMeta> {
  return {
    "/": {
      title: "Science-based adaptive training",
      description:
        "Science-based adaptive running plans. Your completed runs, available time and feedback guide adjustments to duration, intensity and recovery. Expected launch: 1 November 2026.",
      canonical: `${SITE_ORIGIN}/`,
    },
    "/method": {
      title: "The PX7 Training Method",
      description:
        "The PX7 Training Method: plan, run, record and adjust. Research guides the sessions; your training and feedback guide duration, intensity and recovery.",
      canonical: `${SITE_ORIGIN}/method`,
    },
    "/science": {
      title: "The science behind PX7 Training",
      description:
        "The studies PX7 Training builds on: long-run injury risk, training by recovery, marathon tapering and heart rate drift, explained in plain language.",
      canonical: `${SITE_ORIGIN}/science`,
    },
    "/about": {
      title: "About Jørgen Larsen",
      description:
        "Meet Jørgen Larsen, founder of LØBEMENTOR and PX7 Training: a digital running product in English, built in Copenhagen for runners internationally.",
      canonical: `${SITE_ORIGIN}/about`,
    },
    "/articles": {
      title: "Articles",
      description:
        "Short articles on new running research about long runs, recovery, tapering and heart rate, and on using your PX7 Training targets.",
      canonical: `${SITE_ORIGIN}/articles`,
    },
    "/pricing": {
      title: "Access and pricing",
      description:
        "Planned launch prices: €24/month for PX7 Training; €89 for an optional written training review. Expected launch: 1 November 2026.",
      canonical: `${SITE_ORIGIN}/pricing`,
    },
    "/newsletter": {
      title: "Newsletter",
      description: "Manage your PX7 Training newsletter subscription.",
      canonical: `${SITE_ORIGIN}/newsletter`,
    },
    "/privacy": {
      title: "Privacy policy",
      description:
        "How PX7 Training uses your personal and training data, which services process it, and how to export or delete it.",
      canonical: `${SITE_ORIGIN}/privacy`,
    },
    "/terms": {
      title: "Terms of service",
      description:
        "Subscription, payment, cancellation and account terms for PX7 Training.",
      canonical: `${SITE_ORIGIN}/terms`,
    },
    "/cookies": {
      title: "Cookies",
      description:
        "How PX7 Training uses sign-in cookies, payment and bot-protection services, fonts and site analytics.",
      canonical: `${SITE_ORIGIN}/cookies`,
    },
  };
}

export function getRouteMeta(pathname: string, rolloutMode: RolloutMode): RouteMeta | null {
  const path = normalizePath(pathname);

  if (path.startsWith("/articles/")) {
    const slug = path.slice("/articles/".length);
    const article = articleMetaBySlug[slug];
    if (!article) return null;
    return {
      title: article.title,
      description: article.description,
      // Article pages are prerendered to articles/<slug>/index.html, which
      // Cloudflare serves at the trailing-slash URL and 308-redirects the
      // slash-less one to. The canonical has to name the URL that answers 200.
      canonical: `${SITE_ORIGIN}/articles/${article.slug}/`,
    };
  }

  return staticRouteMeta(rolloutMode)[path] || null;
}

export function toDocumentMeta(meta: RouteMeta) {
  return {
    title: meta.title,
    description: meta.description,
    ogTitle: meta.ogTitle ?? meta.title,
    ogDescription: meta.ogDescription ?? meta.description,
    ogImage: meta.ogImage ?? DEFAULT_OG_IMAGE,
    canonical: meta.canonical,
  };
}

export function formatDocumentTitle(title: string) {
  return `${title} · ${SITE_NAME}`;
}
